import { FiMic } from 'react-icons/fi';

export const nativeLikeRepairData = {
  courseId: 'native-like-repair',
  courseType: 'speaking-course',
  title: 'Native-like Repair',
  icon: FiMic,
  overview: {
    impact: 'Self-correct without freezing so a slip becomes evidence of control, not panic',
    focus: 'or rather, I mean, what I meant was, well actually',
    uses: 'Speaking Parts 1–3 when you choose the wrong word, overgeneralise, or need to narrow a claim mid-answer',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Repair is fluency, not failure',
      content: `<p>Every fluent speaker repairs. A <strong>repair</strong> is a short, spoken edit: you notice a wrong word, a too-strong claim, or a missing detail, and you fix it without stopping the turn. IELTS Fluency and Coherence rewards this when the fix is quick. Long silence, repeated “sorry sorry sorry,” or abandoning the sentence looks like a breakdown.</p>
<p>Band 6 speakers often freeze after a slip because they treat accuracy as all-or-nothing. Band 7–8 speakers keep the rhythm: they mark the edit, replace the problem, and continue. The examiner hears self-monitoring — a feature of natural speech — rather than a memorised script that cannot bend.</p>
<p>Repair is not the same as rambling. You change <em>one</em> piece of information. “The government should, or rather, needs to fund… I mean the local government.” The idea stays; the wording gets sharper. If you restart the whole answer, you have not repaired — you have reset.</p>`,
      points: [
        'Repair = notice → mark → replace → continue. Do not restart the whole answer.',
        'A 0.5-second pause plus a marker sounds native; a 4-second silence sounds stuck.',
        'Apologising three times wastes time and draws attention to the error.',
        'The best repairs make the meaning more precise, not just grammatically safer.',
      ],
      examples: [
        { label: 'Freeze (weaker)', text: 'The government should… um… sorry, wait… I forgot.' },
        { label: 'Repair (stronger)', text: 'The government should, or rather, needs to fund this. I mean the local government.' },
      ],
    },
    {
      title: 'Core repair markers',
      content: `<p>Learn a small set of markers and use them automatically. <strong>Or rather</strong> upgrades a word you just used (“should → needs to”). <strong>I mean</strong> specifies or corrects a noun (“the government → the local government”). <strong>What I meant was</strong> repairs a whole clause after you hear yourself go wrong. <strong>Well, actually</strong> softens a claim you overstated. <strong>Or at least</strong> narrows an absolute.</p>
<p>These markers are short on purpose. Long commentaries (“Let me correct myself because what I was trying to say…”) burn the clock in Part 2 and sound rehearsed in Part 3. Place the marker immediately after the problem word or at the next clause boundary, then give the replacement once.</p>
<p>Intonation matters. Dip slightly on the marker, then stress the new information. “I mean the <em>local</em> government.” If both versions get the same stress, the examiner may not hear the correction.</p>`,
      points: [
        '"Or rather" — swap a verb or adjective for a more accurate one.',
        '"I mean" — specify a noun, place, or group.',
        '"What I meant was" — repair a whole idea you just finished.',
        '"Well, actually" / "or at least" — reduce an overgeneralisation without starting again.',
      ],
      table: {
        headers: ['Marker', 'Job', 'Example'],
        rows: [
          ['or rather', 'Upgrade a word', 'They should, or rather, they need to publish the results.'],
          ['I mean', 'Specify', 'Students struggle with maths — I mean exam maths, not daily arithmetic.'],
          ['What I meant was', 'Repair a clause', 'What I meant was that rural clinics lack staff, not buildings.'],
          ['Well, actually', 'Soften / correct a fact', 'Well, actually, a lot of young people still prefer cash in my city.'],
          ['or at least', 'Narrow an absolute', 'Everyone hates commuting — or at least, most people I know do.'],
        ],
      },
    },
    {
      title: 'What to repair — and what to leave',
      content: `<p>Repair meaning first. Wrong word, wrong group, overgeneralisation, and mixed time (“last year” when you mean “when I was a child”) are worth fixing because they change the answer. Tiny pronunciation slips that stay understandable are usually not worth a repair; chasing every sound destroys fluency.</p>
<p>Grammar repairs should be light. If you said “people is,” you can slide to “people are more likely…” in the next clause without a marker. If you notice a broken conditional, finish a simple sentence instead of rebuilding a long one mid-air. Examiners prefer a short accurate continuation to a long reconstructed monster.</p>
<p>Do not repair opinions into the opposite view. Changing “I agree” to “I completely disagree” after a marker sounds confused. Repair the <em>scope</em> of the opinion (“I agree in cities, I mean large cities”) rather than the side you are on.</p>`,
      points: [
        'Repair: wrong noun, wrong time, too-strong claim, unclear “they/this”.',
        'Usually leave: a slight accent slip the examiner already understood.',
        'Never flip your whole opinion; narrow it instead.',
        'If the sentence is broken, abandon the clause — not the topic — and start a short new sentence.',
      ],
      examples: [
        { label: 'Meaning repair', text: 'Tourists damage the city. I mean the historic centre, not the suburbs.' },
        { label: 'Scope repair', text: 'Students always cheat — or rather, a minority cheat when the stakes are high.' },
        { label: 'Leave it', text: 'I lived in “Libary” — keep going if the word was clear enough.' },
      ],
      warning: 'Repeating “sorry” is not a repair strategy. One marker, one replacement, then move on.',
    },
    {
      title: 'Repair in each speaking part',
      content: `<p>Part 1 answers are short, so keep repairs to a single marker. “I work in marketing — or rather, digital marketing.” Then add one detail. Part 2 is a long turn: if you lose the thread, use a return-plus-repair: “Anyway, what I meant to say about the place is that it felt safe at night.” Do not restart the two-minute talk.</p>
<p>Part 3 is where native-like repair scores most, because the questions are abstract and you will overshoot. Hedge, then repair the hedge if it was too weak or too strong: “I think governments should ban cars in centres. Or rather, they should restrict them during peak hours.” That sequence shows thinking in real time.</p>
<p>Practise with a recording. Speak for 20 seconds, deliberately use a slightly wrong word, then repair. The goal is muscle memory: marker + replacement without a laugh, an apology, or a silence.</p>`,
      points: [
        'Part 1: one-word upgrade, then continue.',
        'Part 2: repair and return to the cue-card point; do not restart.',
        'Part 3: repair overgeneralisation — this is where nuance lives.',
      ],
    },
  ],
  exercises: [
    {
      title: 'Choose the fluent repair',
      type: 'select',
      instructions: 'Select the repair that keeps fluency and fixes the meaning.',
      questions: [
        {
          text: 'A fluent repair uses ____.',
          options: ['or rather / I mean', 'long silence', 'sorry sorry sorry'],
          answer: 'or rather / I mean',
          explanation: 'Short markers keep the turn moving. Silence and stacked apologies draw attention to the slip and lower fluency.',
        },
        {
          text: 'You said “the government” but you mean the city council. What is the best repair?',
          options: [
            'The government should fund this. I mean the local government.',
            'Sorry sorry, wait, I said the wrong thing, can I start again?',
            'The government… [4-second pause] …council?',
          ],
          answer: 'The government should fund this. I mean the local government.',
          explanation: '“I mean” specifies the noun without restarting. Apologies and long pauses waste time and sound less confident.',
        },
        {
          text: 'Which repair upgrades a verb rather than restarting?',
          options: [
            'They should, or rather, they need to publish the data.',
            'They should publish the data. No. Forget that.',
            'They should publish the data sorry sorry they must.',
          ],
          answer: 'They should, or rather, they need to publish the data.',
          explanation: '“Or rather” sits inside the sentence and swaps “should” for a stronger verb. The other options abandon or clutter the turn.',
        },
        {
          text: 'You overstated: “Everyone hates public transport.” Best repair?',
          options: [
            'Everyone hates public transport — or at least, many people in my city do at rush hour.',
            'Everyone hates public transport. I completely disagree with myself.',
            'Everyone hates public transport. Sorry. Next question.',
          ],
          answer: 'Everyone hates public transport — or at least, many people in my city do at rush hour.',
          explanation: 'Narrowing the claim keeps your position and adds precision. Flipping the opinion or giving up looks like a collapse.',
        },
      ],
    },
    {
      title: 'Pick the right marker',
      type: 'select',
      instructions: 'Choose the marker that fits the type of slip.',
      questions: [
        {
          text: 'You used a weak verb and want a stronger one. Best marker?',
          options: ['or rather', 'by the way', 'in conclusion'],
          answer: 'or rather',
          explanation: '“Or rather” is the standard upgrade marker. “By the way” digresses; “in conclusion” belongs at the end of a long turn, not inside a repair.',
        },
        {
          text: 'You named the wrong group. Best marker?',
          options: ['I mean', 'firstly', 'as a result'],
          answer: 'I mean',
          explanation: '“I mean” specifies or replaces a noun. “Firstly” sequences ideas; “as a result” shows cause, not correction.',
        },
        {
          text: 'You finished a sentence and then heard that the whole idea was wrong. Best marker?',
          options: ['What I meant was', 'for example', 'to be honest I will stop'],
          answer: 'What I meant was',
          explanation: 'A clause-level repair needs a clause-level marker. “For example” adds support; stopping is not a repair.',
        },
        {
          text: 'Which move is usually NOT worth a repair?',
          options: [
            'A slight pronunciation slip the examiner already understood',
            'Saying “last year” when you mean “when I was ten”',
            'Saying “all students cheat” when you mean “some students”',
          ],
          answer: 'A slight pronunciation slip the examiner already understood',
          explanation: 'Chasing tiny sound errors kills fluency. Wrong time and overgeneralisation change meaning, so those are worth fixing.',
        },
      ],
    },
    {
      title: 'Complete the repair',
      type: 'fill-in',
      instructions: 'Type the missing repair marker. Use the exact words from the lesson.',
      questions: [
        {
          text: 'The government should, ___ ___, need to fund night buses. (upgrade “should”)',
          answer: 'or rather',
          explanation: '“Or rather” is the marker that replaces a word you have just used with a more accurate one.',
        },
        {
          text: 'Parents are struggling — ___ ___ with the cost of childcare, not with school itself.',
          answer: 'I mean',
          explanation: '“I mean” specifies which part of the previous noun or idea you want the listener to keep.',
        },
        {
          text: '___ ___ ___ ___ that rural hospitals lack staff, not buildings.',
          answer: 'What I meant was',
          explanation: 'This frame repairs a complete idea after you have already finished the first version.',
        },
        {
          text: 'Young people never read books — ___ ___ ___, many still read on their phones.',
          answer: 'or at least',
          explanation: '“Or at least” narrows an absolute so the claim becomes defensible without changing topic.',
        },
      ],
    },
  ],
  tips: [
    'Use native-like repair the same day: speak one sentence, slip on purpose, then fix it with “or rather” or “I mean.”',
    'One marker, one replacement, then continue — never stack apologies.',
    'Repair meaning and scope first; ignore tiny slips the listener already understood.',
    'In Part 2, repair and return to the cue card. Do not restart the two minutes.',
  ],
  dailyPractice: [
    'Record a 20-second Part 1 answer and insert one planned repair with “I mean.”',
    'Take an absolute headline (“Everyone hates X”) and say it, then repair it with “or at least.”',
    'Practise the four-step loop: notice → mark → replace → continue, five times in a row.',
    'Listen to a podcast interview and write down three genuine repairs the speaker makes.',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext: 'Native-like Repair speaking course for IELTS. Teaches fluent self-correction with markers such as or rather, I mean, what I meant was, well actually, and or at least. Covers what to repair versus leave, how to avoid freezes and apology loops, and how to use repair in Parts 1–3 without restarting the answer.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...nativeLikeRepairData,
      title: nativeLikeRepairData.title + ' (Foundations)',
      overview: { ...nativeLikeRepairData.overview, duration: '15-20 minutes' },
      sections: nativeLikeRepairData.sections.slice(0, Math.max(2, Math.ceil(nativeLikeRepairData.sections.length / 2))),
      exercises: nativeLikeRepairData.exercises.slice(0, 1).map((ex) => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...nativeLikeRepairData,
      title: nativeLikeRepairData.title + ' (Advanced)',
      overview: { ...nativeLikeRepairData.overview, duration: '30-35 minutes' },
      sections: nativeLikeRepairData.sections,
      exercises: nativeLikeRepairData.exercises.map((ex) => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(nativeLikeRepairData.tips || []),
        'For Band 8+: repair without naming the error — just replace and continue.',
        'Advanced tip: combine repair with a reason so the correction adds content, not only grammar.',
      ],
    };
  }
  return nativeLikeRepairData;
}
